import { useEffect, useState } from "react";
import { api, type Account, type Role } from "./api";
import { Link } from "./Link";
import { formatWhen, statusLabels, type ReviewSummary } from "./review-api";
import { Field, Notice } from "./ui";

interface PlayedTournament {
  id: string;
  name: string;
  status: string;
  teamName: string;
  date: string | null;
  position: number | null;
  points: number | null;
  expiresAt: string | null;
}
interface ProfileData {
  account: Account;
  player: { id: string; name: string; points: number; tournaments: PlayedTournament[] } | null;
  claim: { id: string; status: "pending" | "declined"; playerName: string } | null;
}
interface Candidate {
  id: string;
  name: string;
  tournaments: string[];
}

export const roleLabels: Record<Role, string> = {
  admin: "Admin",
  coach: "Coach",
  player: "Player",
};

const ordinal = (n: number) => {
  const rule = new Intl.PluralRules("en-US", { type: "ordinal" }).select(n);
  return `${n}${{ one: "st", two: "nd", few: "rd" }[rule as "one"] ?? "th"}`;
};

export function Profile({
  account,
  go,
  accountChanged,
  signOutEverywhere,
}: {
  account: Account;
  go: (href: string) => void;
  accountChanged: () => Promise<void>;
  signOutEverywhere: () => Promise<void>;
}) {
  const [data, setData] = useState<ProfileData | null>(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let current = true;
    setError("");
    api<ProfileData>("/me")
      .then((result) => current && setData(result))
      .catch((e: Error) => current && setError(e.message));
    return () => {
      current = false;
    };
  }, [reload]);
  const refresh = () => setReload((n) => n + 1);
  return (
    <main id="main" className="app-main">
      <div className="tournament-heading">
        <p className="eyebrow">{account.roles.map((role) => roleLabels[role]).join(" · ") || "Account"}</p>
        <h1>{data?.account.name ?? account.name ?? "Your profile"}</h1>
        <p>{account.email}</p>
      </div>
      {error ? (
        <Notice error>
          {error}{" "}
          <button className="text-button" onClick={refresh}>
            Retry
          </button>
        </Notice>
      ) : !data ? (
        <Notice>Loading your profile…</Notice>
      ) : (
        <div className="profile-layout">
          <div className="stack profile-main">
            <VideoReviews go={go} />
            {data.player ? (
              <Ranking player={data.player} go={go} />
            ) : (
              <Claim claim={data.claim} changed={refresh} />
            )}
          </div>
          <AccountDetails
            name={data.account.name}
            saved={async () => {
              refresh();
              await accountChanged();
            }}
            signOutEverywhere={signOutEverywhere}
          />
        </div>
      )}
    </main>
  );
}

function Ranking({
  player,
  go,
}: {
  player: NonNullable<ProfileData["player"]>;
  go: (href: string) => void;
}) {
  return (
    <section className="stack">
      <div className="ranking-total">
        <strong>{player.points.toLocaleString()}</strong>
        <span>ranking points as {player.name}</span>
      </div>
      <p className="muted">
        Each finish counts for one year from the day it was awarded.
      </p>
      <h2>Your tournaments</h2>
      {player.tournaments.length === 0 ? (
        <p>No published tournaments yet. Your results appear here after your first one.</p>
      ) : (
        <ul className="plain-list played-list">
          {player.tournaments.map((t) => (
            <li key={t.id}>
              <div>
                <Link go={go} href={`/t/${t.id}`}>
                  {t.name}
                </Link>
                <small>
                  {[t.date, t.teamName].filter(Boolean).join(" · ")}
                </small>
              </div>
              <span className="played-result">
                {t.position
                  ? `${ordinal(t.position)} · ${(t.points ?? 0).toLocaleString()} pts`
                  : t.status === "finished"
                    ? "Finished"
                    : "In progress"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Claim({
  claim,
  changed,
}: {
  claim: ProfileData["claim"];
  changed: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Candidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (claim?.status === "pending")
    return (
      <section className="stack">
        <h2>Waiting for approval</h2>
        <p>
          You asked to link the tournament record <strong>{claim.playerName}</strong>.
          An admin will check it and your results will appear here once it's approved.
        </p>
        {error && <Notice error>{error}</Notice>}
        <div className="actions">
          <button
            className="text-button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api("/me/claim", {}, "DELETE");
                changed();
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Withdraw request
          </button>
        </div>
      </section>
    );
  return (
    <section className="stack">
      <h2>Find your tournament record</h2>
      <p>
        Results are recorded under the name you played with. Search for it and
        ask us to link it to your account.
      </p>
      {claim?.status === "declined" && (
        <Notice>
          Your request for {claim.playerName} was declined. If that's a mistake,
          reply to any Spike Easy email or search again.
        </Notice>
      )}
      <form
        className="player-toolbar"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
            const { players } = await api<{ players: Candidate[] }>(
              `/me/claim-candidates?q=${encodeURIComponent(query.trim())}`,
            );
            setResults(players);
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Name you played under">
          <input
            type="search"
            minLength={2}
            maxLength={60}
            required
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
        <button className="secondary" disabled={busy || query.trim().length < 2}>
          {busy ? "Searching…" : "Search"}
        </button>
      </form>
      {error && <Notice error>{error}</Notice>}
      {results &&
        (results.length === 0 ? (
          <p>
            No unlinked record matches “{query.trim()}”. Try your first or last
            name only.
          </p>
        ) : (
          <ul className="player-directory">
            {results.map((player) => (
              <li key={player.id}>
                <div>
                  <strong>{player.name}</strong>
                  <small>{player.tournaments.join(" · ")}</small>
                </div>
                <button
                  className="text-button"
                  disabled={busy}
                  aria-label={`Claim ${player.name}`}
                  onClick={async () => {
                    setBusy(true);
                    setError("");
                    try {
                      await api("/me/claim", { playerId: player.id });
                      changed();
                    } catch (e) {
                      setError((e as Error).message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  This is me
                </button>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}

function AccountDetails({
  name,
  saved,
  signOutEverywhere,
}: {
  name: string | null;
  saved: () => Promise<void>;
  signOutEverywhere: () => Promise<void>;
}) {
  const [value, setValue] = useState(name ?? "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  return (
    <div className="stack profile-account">
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          setMessage("");
          try {
            await api("/me", { name: value.trim() }, "PUT");
            setMessage("Name saved.");
            await saved();
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <h2>Account</h2>
        <Field
          label="Your name"
          hint="Shown to Spike Easy staff. Tournament records keep the name the organizer entered."
        >
          <input
            autoComplete="name"
            required
            maxLength={80}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        {message && <Notice>{message}</Notice>}
        {error && <Notice error>{error}</Notice>}
        <div className="actions">
          <button className="secondary" disabled={busy || !value.trim() || value.trim() === name}>
            {busy ? "Saving…" : "Save name"}
          </button>
        </div>
      </form>
      <SignOutEverywhere signOut={signOutEverywhere} />
    </div>
  );
}

function SignOutEverywhere({ signOut }: { signOut: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <section className="stack">
      <h2>Devices</h2>
      <p className="muted">
        Signs you out on every device, this one included, and cancels any
        sign-in link still in your inbox. Use it after losing a phone or
        signing in on a shared computer.
      </p>
      {error && <Notice error>{error}</Notice>}
      <div className="actions">
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError("");
            try {
              await signOut();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          {busy ? "Signing out…" : "Sign out everywhere"}
        </button>
      </div>
    </section>
  );
}

/** Only shown to people who have a review, so tournament-only players see no empty section. */
function VideoReviews({ go }: { go: (href: string) => void }) {
  const [reviews, setReviews] = useState<ReviewSummary[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let current = true;
    api<{ reviews: ReviewSummary[] }>("/me/reviews")
      .then((result) => current && setReviews(result.reviews))
      .catch((e: Error) => current && setError(e.message));
    return () => {
      current = false;
    };
  }, []);
  if (error) return <Notice error>Your video reviews could not be loaded. {error}</Notice>;
  if (!reviews || reviews.length === 0) return null;
  return (
    <section className="stack">
      <h2>Your video reviews</h2>
      <ul className="plain-list played-list">
        {reviews.map((review) => (
          <li key={review.id}>
            <div>
              <Link go={go} href={`/me/reviews/${review.id}`}>
                {review.status === "awaiting_upload"
                  ? "Upload your footage"
                  : review.status === "done"
                    ? "Watch your review"
                    : "Footage received"}
              </Link>
              <small>Opened {formatWhen(review.createdAt)}</small>
            </div>
            <span className="played-result">{review.status === "awaiting_upload" ? statusLabels.awaiting_upload : review.status === "done" ? "Ready" : "With the coach"}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
